import { useState, useMemo } from "react";
import Map, { Marker, Popup, NavigationControl } from "react-map-gl/maplibre";
import { Box, Text, Center, Paper, Stack } from "@mantine/core";
import { IconHome, IconBuilding } from "@tabler/icons-react";
import type { Apartment } from "@/api/types";
import "maplibre-gl/dist/maplibre-gl.css";

interface ApartmentDetailMapProps {
  apartment: Apartment;
  officeCoords?: { latitude: number; longitude: number } | null;
  height?: number | string;
}

export function ApartmentDetailMap({
  apartment,
  officeCoords,
  height = 320,
}: ApartmentDetailMapProps) {
  const [showPopup, setShowPopup] = useState(true);

  // Calculate center point
  const center = useMemo(() => {
    if (!apartment.latitude || !apartment.longitude) {
      return { longitude: 18.0686, latitude: 59.3293 };
    }
    if (officeCoords) {
      // Center between apartment and office
      return {
        longitude: (apartment.longitude + officeCoords.longitude) / 2,
        latitude: (apartment.latitude + officeCoords.latitude) / 2,
      };
    }
    return { longitude: apartment.longitude, latitude: apartment.latitude };
  }, [apartment.latitude, apartment.longitude, officeCoords]);

  // Calculate zoom level based on distance between points
  const zoom = useMemo(() => {
    if (!apartment.latitude || !apartment.longitude || !officeCoords) {
      return 14;
    }
    const latDiff = Math.abs(apartment.latitude - officeCoords.latitude);
    const lngDiff = Math.abs(apartment.longitude - officeCoords.longitude);
    const maxDiff = Math.max(latDiff, lngDiff);

    // Approximate zoom level based on coordinate span
    if (maxDiff > 0.1) return 11;
    if (maxDiff > 0.05) return 12;
    if (maxDiff > 0.02) return 13;
    return 14;
  }, [apartment.latitude, apartment.longitude, officeCoords]);

  if (!apartment.latitude || !apartment.longitude) {
    return (
      <Box
        h={height}
        style={{
          backgroundColor: "#0f3460",
          borderRadius: "0.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text c="dimmed">No location data available</Text>
      </Box>
    );
  }

  return (
    <Box h={height} style={{ borderRadius: "0.5rem", overflow: "hidden" }}>
      <Map
        initialViewState={{
          longitude: center.longitude,
          latitude: center.latitude,
          zoom: zoom,
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle="https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
        scrollZoom={false}
      >
        <NavigationControl position="top-right" />

        {/* Apartment marker */}
        <Marker longitude={apartment.longitude} latitude={apartment.latitude}>
          <ApartmentMarker onClick={() => setShowPopup(!showPopup)} />
        </Marker>

        {showPopup && (
          <Popup
            longitude={apartment.longitude}
            latitude={apartment.latitude}
            onClose={() => setShowPopup(false)}
            closeButton={true}
            anchor="bottom"
            offset={20}
          >
            <Paper
              p="sm"
              style={{
                minWidth: 180,
                backgroundColor: "#16213e",
                border: "1px solid #0f3460",
              }}
            >
              <Stack gap="xs">
                <Text size="sm" fw={600} c="#eeeeee" lineClamp={2}>
                  {apartment.title}
                </Text>
                {apartment.address && (
                  <Text size="xs" c="dimmed">
                    {apartment.address}
                  </Text>
                )}
              </Stack>
            </Paper>
          </Popup>
        )}

        {/* Office marker */}
        {officeCoords && (
          <Marker longitude={officeCoords.longitude} latitude={officeCoords.latitude}>
            <OfficeMarker />
          </Marker>
        )}
      </Map>
    </Box>
  );
}

// Apartment marker component
function ApartmentMarker({ onClick }: { onClick: () => void }) {
  return (
    <Center
      onClick={onClick}
      style={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        backgroundColor: "#e94560",
        border: "3px solid white",
        boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
        cursor: "pointer",
        transition: "transform 0.1s ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
    >
      <IconHome size={16} color="white" />
    </Center>
  );
}

// Office marker component
function OfficeMarker() {
  return (
    <Center
      style={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        backgroundColor: "#4caf50",
        border: "3px solid white",
        boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
      }}
    >
      <IconBuilding size={16} color="white" />
    </Center>
  );
}
